<template>
  <div>
    <div class="absolute-elements">
      <NavBar :sections="sections" @scroll-to-section="scrollToSection" />
    </div>
    <div class="h-screen w-screen overflow-y-auto bg-base-100" @scroll="handleScroll">
      <WaveAnimation :visible="showWave" />
      <!-- Hero Section -->
      <section class="section relative overflow-hidden" id="slide1">
        <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
        <div class="absolute inset-0 bg-pattern opacity-10"></div>
        
        <!-- Decorative Elements -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-primary/20 rounded-full blur-xl animate-float"></div>
        <div class="absolute bottom-20 right-20 w-48 h-48 bg-secondary/10 rounded-full blur-2xl animate-float-slow"></div>
        
        <div class="relative z-10 flex items-center justify-center min-h-screen px-6">
          <div class="text-center max-w-4xl mx-auto">
            <h1 class="text-5xl md:text-7xl font-bold text-white mb-6">
              Parts <span class="text-primary">&</span> Service
            </h1>
            <p class="text-xl md:text-2xl text-white/80 mb-8 leading-relaxed">
              DOT Inspections to major Accident Repairs
            </p>
            <p class="text-lg md:text-xl text-white/70 mb-12 max-w-3xl mx-auto">
              With 13 service bays, STE is dedicated to keep your costs down and your stainless steel equipment operating at optimum levels.
            </p>
            
            <!-- CTA Button -->
            <button 
              @click="scrollToNextSection"
              class="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-primary/90 
                     text-white font-semibold rounded-full transition-all duration-300 
                     hover:shadow-lg hover:shadow-primary/30 hover:scale-105"
            >
              <span>Explore Our Services</span>
              <font-awesome-icon :icon="['fas', 'chevron-down']" class="animate-bounce" />
            </button>
          </div>
        </div>
      </section>
      <!-- Service Overview Section -->
      <section class="section" id="slide2">
        <div class="section-content">
          <ServiceOverviewSection />
        </div>
      </section>

      <!-- Service Details Section -->
      <section class="section min-h-screen" id="slide3">
        <div class="section-content">
          <ServiceDetailsSection />
        </div>
      </section>

      <!-- Warranty Centers -->
      <section class="section" id="warranty">
        <div class="section-content">
          <WarrantyCenters />
        </div>
      </section>

      <!-- Contact Form Section -->
      <section class="section" id="slide4">
        <div class="section-content">
          <ServiceContactSection />
        </div>
      </section>

      <!-- Spare Parts Section -->
      <section class="section" id="slide5">
        <div class="section-content">
          <ServicePartsSection />
        </div>
      </section>

      <!-- Contact Section -->
      <section class="section" id="slide6">
        <div class="section-content">
          <ContactSection />
        </div>
      </section>
    </div>

    <!-- Scroll Navigation -->
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollUp"
        :icon="isUpHovered ? 'angles-up' : 'chevron-up'"
        class="scroll-arrow up-arrow fixed bottom-[60px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToPreviousSection"
        @mouseenter="isUpHovered = true"
        @mouseleave="isUpHovered = false"
      />
    </transition>
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollDown"
        :icon="isDownHovered ? 'angles-down' : 'chevron-down'"
        class="scroll-arrow down-arrow fixed bottom-[10px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToNextSection"
        @mouseenter="isDownHovered = true"
        @mouseleave="isDownHovered = false"
      />
    </transition>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue';
import ServiceOverviewSection from '../components/ServiceOverviewSection.vue';
import ServiceDetailsSection from '../components/ServiceDetailsSection.vue';
import ServiceContactSection from '../components/ServiceContactSection.vue';
import WarrantyCenters from '../components/WarrantyCenters.vue';
import ServicePartsSection from '../components/ServicePartsSection.vue';
import ContactSection from '../components/ContactSection.vue';
import WaveAnimation from '../components/WaveAnimation.vue';

export default {
  name: 'PartsService',
  components: {
    NavBar,
    ServiceOverviewSection,
    ServiceDetailsSection,
    ServiceContactSection,
    WarrantyCenters,
    ServicePartsSection,
    ContactSection,
    WaveAnimation
  },
  data() {
    return {
      sections: [
        { name: 'CAPABILITIES', id: 'slide2' },
        { name: 'SERVICES', id: 'slide3' },
        { name: 'WARRANTY', id: 'warranty' },
        { name: 'CALL BACK', id: 'slide4' },
        { name: 'PARTS', id: 'slide5' },
        { name: 'CONTACT', id: 'slide6' }
      ],
      showScrollUp: false,
      showScrollDown: true,
      isUpHovered: false,
      isDownHovered: false,
      showWave: false
    };
  },  mounted() {
    // Initialize scroll state
    const container = document.querySelector('.overflow-y-auto');
    if (container) {
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    }
    if (this.$route.hash === '#warranty') {
      this.$nextTick(() => this.scrollToSection('warranty'));
    }
  },
  methods: {
    handleScroll(event) {
      const container = event.target;
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    },
    
    scrollToSection(id) {
      console.log(`Scrolling to section: ${id}`);
      const container = document.querySelector('.overflow-y-auto');
      const element = document.getElementById(id);
      if (element) {
        container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      }
    },
    
    scrollToNextSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      const nextSection = sections.find(
        (section) => section.offsetTop > currentScroll + tolerance
      );

      if (nextSection) {
        console.log(`Scrolling to next section: ${nextSection.id}`);
        container.scrollTo({ top: nextSection.offsetTop, behavior: 'smooth' });
      } else {
        console.log('No next section found');
      }
    },    
    scrollToPreviousSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      let previousSection = null;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.offsetTop + tolerance < currentScroll) {
          previousSection = section;
          break;
        }
      }

      if (previousSection) {
        console.log(`Scrolling to previous section: ${previousSection.id}`);
        container.scrollTo({ top: previousSection.offsetTop, behavior: 'smooth' });
      } else {
        this.scrollToTop();
      }
    },
    
    scrollToTop() {
      const container = document.querySelector('.overflow-y-auto');
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
};
</script>

<style scoped>
.absolute-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  z-index: 100;
}

.h-screen.overflow-y-auto {
  background: radial-gradient(ellipse at bottom, var(--color-base-100) 0%, var(--color-base-300) 90%);
  background-attachment: fixed;
  position: relative;
  scrollbar-width: none;
  z-index: 0;
  -webkit-overflow-scrolling: touch;
}

.section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  width: 100%;
  overflow: hidden;
}

.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
}

@keyframes float-slow {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-30px) rotate(-3deg); }
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-slow {
  animation: float-slow 12s ease-in-out infinite;
}

.section-content {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
